import React, { useState, useEffect } from 'react';
import {
	Box,
	Container,
	TextField,
	Button,
	Typography,
	Grid,
	Avatar,
	IconButton,
	Paper,
	Tab,
	Tabs,
	InputAdornment,
	Snackbar,
	Alert,
	List,
	ListItem,
} from '@mui/material';
/* import { styled } from '@mui/material/styles'; */
import PersonIcon from '@mui/icons-material/Person';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { connect } from 'react-redux';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import NoteIcon from '@mui/icons-material/Note';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CodeSnippet from '../../components/VcardCode';

/* const Input = styled('input')({
	display: 'none',
}); */

function VCardMaker() {
	const [formData, setFormData] = useState({
		firstName: '',
		secondName: '',
		phoneNumber: '',
		email: '',
		companyName: '',
		position: '',
		note: '',
	});
	const [profilePhoto, setProfilePhoto] = useState(null);
	const [tabValue, setTabValue] = useState(0);
	const [vcardCode, setVcardCode] = useState('');
	const [jsonCode, setJsonCode] = useState('');
	const [openSnackbar, setOpenSnackbar] = useState(false);

	const handleChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	const handlePhotoUpload = (e) => {
		if (e.target.files[0]) {
			const reader = new FileReader();
			reader.onloadend = () => {
				setProfilePhoto(reader.result);
			};
			reader.readAsDataURL(e.target.files[0]);
		}
	};

	const handleTabChange = (event, newValue) => {
		setTabValue(newValue);
	};

	useEffect(() => {
		if (formData.firstName && formData.phoneNumber) {
			generateVCard();
		} else {
			setVcardCode('');
			setJsonCode('');
		}
	}, [formData, profilePhoto]);

	const generateVCard = () => {
		const vcard = [
			'BEGIN:VCARD',
			'VERSION:3.0',
			`FN;CHARSET=UTF-8:${formData.firstName} ${formData.secondName}`,
			`N;CHARSET=UTF-8:;${formData.secondName};${formData.firstName};;`,
			formData.email && `EMAIL;CHARSET=UTF-8;type=WORK,INTERNET:${formData.email}`,
			profilePhoto && `PHOTO;ENCODING=b;TYPE=PNG:${profilePhoto.split(',')[1]}`,
			`TEL;TYPE=WORK,VOICE:${formData.phoneNumber}`,
			formData.companyName && `ORG;CHARSET=UTF-8:${formData.companyName}`,
			formData.note && `NOTE;CHARSET=UTF-8:${formData.note}`,
			'END:VCARD',
		]
			.filter(Boolean)
			.join('\n');

		setVcardCode(vcard);

		const jsonData = {
			vcard: Object.entries({
				firstName: formData.firstName,
				lastName: formData.secondName,
				phone: formData.phoneNumber,
				email: formData.email,
				organization: formData.companyName,
				note: formData.note,
				position: formData.position,
			}).reduce((acc, [key, value]) => {
				if (value) acc[key] = value;
				return acc;
			}, {}),
		};

		if (profilePhoto) {
			jsonData.vcard.photo = profilePhoto;
		}

		const json = JSON.stringify(jsonData, null, 2);
		setJsonCode(json);
	};

	const handleDownload = () => {
		const element = document.createElement('a');
		const file = new Blob([vcardCode], { type: 'text/vcard' });
		element.href = URL.createObjectURL(file);
		element.download = 'contact.vcf';
		document.body.appendChild(element);
		element.click();
		document.body.removeChild(element);
	};

	const handleCopy = () => {
		const codeToCopy = tabValue === 0 ? vcardCode.replace(/\n/g, '\\n') : jsonCode;
		navigator.clipboard
			.writeText(codeToCopy)
			.then(() => {
				setOpenSnackbar(true);
			})
			.catch((err) => {
				console.error('Failed to copy: ', err);
			});
	};

	const handleCloseSnackbar = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		setOpenSnackbar(false);
	};
	return (
		<Container maxWidth="xl">
			<Typography variant="h4" gutterBottom>
				VCard Maker
			</Typography>
			<Typography variant="body1" gutterBottom>
				VCards are the most effective way to share your contact information with customers. They allow people to add you to their WhatsApp address book
				with a single click.
			</Typography>
			<Typography variant="body1" gutterBottom>
				Being in your customer's address book offers several advantages:
				<List>
					<ListItem sx={{ py: 0 }}>You appear in chats with your company name</ListItem>
					<ListItem sx={{ py: 0 }}>Your customers can see your statusupdates</ListItem>
					<ListItem sx={{ py: 0 }}>You reduce the risk of your number being blocked during mass messaging</ListItem>
				</List>
			</Typography>
			<Typography variant="body1" gutterBottom>
				To send a business card, use this curl:
				<CodeSnippet />
			</Typography>
			<Typography variant="body1" gutterBottom>
				For more information, please go to{' '}
				<a href="https://console.maytapi.com/developers/documentation">https://console.maytapi.com/developers/documentation</a>
			</Typography>
			<Grid container spacing={3} sx={{ py: 2 }}>
				<Grid item xs={12} md={6}>
					<Paper elevation={3} sx={{ p: 3 }}>
						<Typography variant="h6" gutterBottom>
							Your Contact Information
						</Typography>
						<Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
							<input accept="image/*" id="icon-button-file" type="file" style={{ display: 'none' }} onChange={handlePhotoUpload} />
							<label htmlFor="icon-button-file">
								<IconButton color="primary" aria-label="upload picture" component="span">
									<Avatar src={profilePhoto} sx={{ width: 100, height: 100 }}>
										<PersonIcon fontSize="large" />
									</Avatar>
								</IconButton>
							</label>
							<Tooltip title="Images should be in JPEG/JPG, PNG format and not larger than 2MB of space. For better view in WhatsApp, better use squared images.">
								<InfoOutlinedIcon sx={{ ml: 1, color: 'text.secondary' }} />
							</Tooltip>
						</Box>
						<Typography variant="subtitle1">Profile Photo</Typography>
						<Grid container spacing={2}>
							<Grid item xs={6}>
								<TextField
									fullWidth
									label="First Name"
									name="firstName"
									value={formData.firstName}
									onChange={handleChange}
									margin="normal"
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<PersonIcon />
											</InputAdornment>
										),
									}}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									fullWidth
									label="Second Name"
									name="secondName"
									value={formData.secondName}
									onChange={handleChange}
									margin="normal"
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<PersonIcon />
											</InputAdornment>
										),
									}}
								/>
							</Grid>
						</Grid>
						<TextField
							fullWidth
							label="Phone Number"
							name="phoneNumber"
							value={formData.phoneNumber}
							onChange={handleChange}
							margin="normal"
							required
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<PhoneIcon />
									</InputAdornment>
								),
							}}
						/>
						<TextField
							fullWidth
							label="Email"
							name="email"
							value={formData.email}
							onChange={handleChange}
							margin="normal"
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<EmailIcon />
									</InputAdornment>
								),
							}}
						/>
						<Grid container spacing={2}>
							<Grid item xs={6}>
								<TextField
									fullWidth
									label="Company Name"
									name="companyName"
									value={formData.companyName}
									onChange={handleChange}
									margin="normal"
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<BusinessIcon />
											</InputAdornment>
										),
									}}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									fullWidth
									label="Your Position"
									name="position"
									value={formData.position}
									onChange={handleChange}
									margin="normal"
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<WorkIcon />
											</InputAdornment>
										),
									}}
								/>
							</Grid>
						</Grid>
						<TextField
							fullWidth
							label="Note"
							name="note"
							value={formData.note}
							onChange={handleChange}
							margin="normal"
							multiline
							rows={4}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<NoteIcon />
									</InputAdornment>
								),
							}}
						/>
					</Paper>
				</Grid>
				<Grid item xs={12} md={6}>
					<Paper elevation={3}>
						<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
							<Tabs value={tabValue} onChange={handleTabChange} aria-label="code preview tabs">
								<Tab label="VCF" />
								<Tab label="JSON" />
							</Tabs>
						</Box>
						<Box sx={{ p: 3 }}>
							<Typography variant="h6" gutterBottom>
								Code Preview
							</Typography>
							{!formData.firstName || !formData.phoneNumber ? (
								<Alert severity="warning" sx={{ mb: 2 }}>
									You have to fill First Name and Phone Number to get code
								</Alert>
							) : (
								<TextField
									fullWidth
									multiline
									rows={10}
									value={tabValue === 0 ? vcardCode.replace(/\n/g, '\\n') : jsonCode}
									InputProps={{
										readOnly: true,
									}}
								/>
							)}
							<Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
								<Button
									variant="contained"
									startIcon={<CloudDownloadIcon />}
									sx={{ mr: 1 }}
									onClick={handleDownload}
									disabled={!formData.firstName || !formData.phoneNumber}
								>
									Download in VCF format
								</Button>
								<Button
									variant="contained"
									startIcon={<ContentCopyIcon />}
									onClick={handleCopy}
									disabled={!formData.firstName || !formData.phoneNumber}
								>
									Copy
								</Button>
							</Box>
						</Box>
					</Paper>
				</Grid>
			</Grid>
			<Snackbar
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				open={openSnackbar}
				autoHideDuration={3000}
				onClose={handleCloseSnackbar}
				message={
					<span style={{ display: 'flex', alignItems: 'center' }}>
						<CheckCircleOutlineIcon style={{ marginRight: '8px' }} />
						Copied to clipboard!
					</span>
				}
				action={
					<IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
						<CloseIcon fontSize="small" />
					</IconButton>
				}
			/>
		</Container>
	);
}

const stateToProps = (state) => ({
	access: state.access,
	location: state.router.location,
	product: state.product.maytapi,
	user: state.access.user.loggedUserObj,
});

export default connect(stateToProps)(VCardMaker);
